/**
 * Where a tool call's result came from, as chips beside its step: a simulated write, a mock (answered
 * from the real ticket's recording or from the scenario world) or the live tool, and whether the result
 * was then bent to the scenario type. The simulator's timeline and a back-testing run's thread read the same flags.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface ToolChipProps {
  className: string;
  title?: string;
  children: ReactNode;
}

export const ToolChip = ({ className, title, children }: ToolChipProps) => (
  <span
    title={title}
    className={cn('ml-1.5 inline-flex items-center rounded-full border px-1.5 align-middle text-[9px] font-medium', className)}
  >
    {children}
  </span>
);

interface ToolProvenanceProps {
  simulated?: boolean;
  grounded?: boolean;
  simulatedWrite?: boolean;
  /** In a replay, the mock answered with the real ticket's own result. */
  recorded?: boolean | null;
  /** Why the mock answered as it did, shown when pointing at its chip. */
  why?: string;
  /** Production pauses this step for a human. */
  gated?: boolean;
}

const Source = ({ simulated, grounded, simulatedWrite, recorded, why }: ToolProvenanceProps) => {
  if (simulatedWrite) return <ToolChip className='border-amber-300 bg-amber-50 text-amber-800'>simulated write</ToolChip>;
  if (simulated) {
    return (
      <ToolChip className='border-gray-500 bg-muted/60 text-muted-foreground' title={why}>
        {recorded ? 'mocked · recorded' : grounded ? 'mocked · bent' : 'mocked'}
      </ToolChip>
    );
  }
  if (grounded) return <ToolChip className='border-emerald-200 bg-emerald-50 text-emerald-700'>live · bent</ToolChip>;
  return <ToolChip className='border-indigo-200 bg-indigo-50 text-indigo-700'>live</ToolChip>;
};

export const ToolProvenance = ({ gated, ...flags }: ToolProvenanceProps) => (
  <>
    <Source {...flags} />
    {gated && (
      <ToolChip
        className='border-blue-200 bg-blue-50 text-blue-700'
        title='In production, a human approves this step before it runs. Nothing ran here.'
      >
        human gated
      </ToolChip>
    )}
  </>
);
